import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Copy, Check, Trash2, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activationLogs, setActivationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [copied, setCopied] = useState(null);
  const [formData, setFormData] = useState({});

  const canEdit = user?.role === 'admin' || user?.role === 'operator';
  const canDelete = user?.role === 'admin';

  useEffect(() => {
    fetchProfile();
    fetchActivationLogs();
  }, [fetchProfile, fetchActivationLogs]);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get(`/profiles/${id}`);
      setProfile(response.data);
      setFormData({
        carrier: response.data.carrier,
        activation_policy: response.data.activation_policy,
        status: response.data.status,
        compliance_notes: response.data.compliance_notes,
      });
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchActivationLogs = useCallback(async () => {
    try {
      const response = await api.get(`/profiles/${id}/activation-logs`);
      setActivationLogs(response.data);
    } catch (error) {
      console.error('Failed to load activation logs');
    }
  }, [id]);

  const handleUpdate = async () => {
    try {
      await api.put(`/profiles/${id}`, formData);
      toast.success('Profile updated successfully');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/profiles/${id}`);
      toast.success('Profile deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]',
      inactive: 'bg-gray-200 text-gray-700 border-gray-400',
      pending: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]',
      suspended: 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]',
      success: 'bg-[#10B981]/10 text-[#10B981]',
      failed: 'bg-[#EF4444]/10 text-[#EF4444]',
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="profile-detail-page">
      <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Profile Details
            </h1>
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              View and manage eSIM profile information
            </p>
          </div>
          <div className="flex gap-2">
            {canEdit && !editMode && (
              <Button onClick={() => setEditMode(true)} className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="edit-button">
                Edit Profile
              </Button>
            )}
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="delete-button">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Profile</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this profile? This action cannot be undone and will also delete all activation logs.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <div className="grid gap-6">
          <Card className="border-[#E2E8F0]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Information</CardTitle>
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${getStatusColor(profile.status)}`}>
                  {profile.status}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {editMode ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Carrier</Label>
                      <Select value={formData.carrier} onValueChange={(value) => setFormData({ ...formData, carrier: value })}>
                        <SelectTrigger data-testid="edit-carrier-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MPT">MPT</SelectItem>
                          <SelectItem value="ATOM">ATOM</SelectItem>
                          <SelectItem value="U9">U9</SelectItem>
                          <SelectItem value="MYTEL">MYTEL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Activation Policy</Label>
                      <Select
                        value={formData.activation_policy}
                        onValueChange={(value) => setFormData({ ...formData, activation_policy: value })}
                      >
                        <SelectTrigger data-testid="edit-policy-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="automatic">Automatic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="edit-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Compliance Notes</Label>
                    <Textarea
                      value={formData.compliance_notes}
                      onChange={(e) => setFormData({ ...formData, compliance_notes: e.target.value })}
                      rows={4}
                      data-testid="edit-compliance-notes"
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button onClick={handleUpdate} className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="save-button">
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditMode(false)} data-testid="cancel-edit-button">
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                        ICCID
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-base font-mono">{profile.iccid}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => copyToClipboard(profile.iccid, 'iccid')}
                          data-testid="copy-iccid-button"
                        >
                          {copied === 'iccid' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                        EID
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-base font-mono">{profile.eid}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => copyToClipboard(profile.eid, 'eid')}
                          data-testid="copy-eid-button"
                        >
                          {copied === 'eid' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                        CARRIER
                      </p>
                      <p className="text-base font-medium text-[#2563EB]">{profile.carrier}</p>
                    </div>

                    <div>
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                        ACTIVATION POLICY
                      </p>
                      <p className="text-base">{profile.activation_policy}</p>
                    </div>
                  </div>

                  {profile.compliance_notes && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
                        COMPLIANCE NOTES
                      </p>
                      <p className="text-sm">{profile.compliance_notes}</p>
                    </div>
                  )}

                  <div className="pt-4 border-t border-[#E2E8F0]">
                    <p className="text-xs text-muted-foreground mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Created: {new Date(profile.created_at).toLocaleString()} | Updated: {new Date(profile.updated_at).toLocaleString()}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {profile.activation_link && (
            <Card className="border-[#E2E8F0] bg-[#F8FAFC]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <ExternalLink className="h-5 w-5" />
                  Activation Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Input value={profile.activation_link} readOnly className="font-mono text-sm" data-testid="activation-link-input" />
                  <Button
                    onClick={() => copyToClipboard(profile.activation_link, 'link')}
                    className="bg-[#2563EB] hover:bg-[#2563EB]/90"
                    data-testid="copy-activation-link-button"
                  >
                    {copied === 'link' ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied === 'link' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-[#E2E8F0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Clock className="h-5 w-5" />
                Activation History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activationLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No activation attempts yet</p>
              ) : (
                <div className="space-y-3">
                  {activationLogs.map((log) => (
                    <div
                      key={log.id}
                      className="p-4 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]"
                      data-testid={`activation-log-${log.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {log.device_eid && (
                          <div>
                            <span className="text-muted-foreground">Device EID:</span>
                            <span className="ml-2 font-mono">{log.device_eid}</span>
                          </div>
                        )}
                        {log.ip_address && (
                          <div>
                            <span className="text-muted-foreground">IP:</span>
                            <span className="ml-2 font-mono">{log.ip_address}</span>
                          </div>
                        )}
                        {log.error_message && (
                          <div className="col-span-2 text-[#EF4444]">
                            Error: {log.error_message}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
